/**
 * CreatorProfileModal - Modal to display creator profile and their cosyflows
 * Compact version with simple clickable cards
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';

// Icons
const CloseIcon = () => (
  <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

// Star icon for earnings rating
const StarIcon = ({ filled }) => (
  <svg
    className="w-4 h-4"
    style={{ color: filled ? '#FFB02E' : 'rgba(255,255,255,0.2)' }}
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={filled ? 0 : 1.5}
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

// Earnings tier configuration
const EARNINGS_TIERS = [
  { threshold: 10000, label: '10K+' },
  { threshold: 20000, label: '20K+' },
  { threshold: 30000, label: '30K+' },
  { threshold: 40000, label: '40K+' },
  { threshold: 50000, label: '50K+' },
];

// Calculate star level based on earnings
const getStarLevel = (earnings) => {
  let level = 0;
  for (const tier of EARNINGS_TIERS) {
    if (earnings >= tier.threshold) {
      level++;
    } else {
      break;
    }
  }
  return level;
};

// Mock earnings generator based on accountNo (for demo purposes)
const getMockEarnings = (accountNo) => {
  if (!accountNo) return 0;
  // Create a pseudo-random but consistent earnings value based on accountNo
  const hash = String(accountNo).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const earnings = (hash * 1337) % 60000; // Random value up to 60K
  return earnings;
};

// Creator Stars Component with tooltip
const CreatorStars = ({ accountNo }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const earnings = getMockEarnings(accountNo);
  const starLevel = getStarLevel(earnings);

  return (
    <div
      className="relative inline-flex items-center gap-0.5 ml-2 cursor-help"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon key={star} filled={star <= starLevel} />
      ))}

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute left-0 top-full mt-2 z-50 bg-dark-blue border border-white/20 rounded-lg p-3 shadow-xl min-w-[180px]">
          <p className="text-xs text-white/60 mb-2 font-medium">Creator Earnings Level</p>
          <div className="space-y-1.5">
            {EARNINGS_TIERS.map((tier, index) => (
              <div key={tier.threshold} className="flex items-center gap-2">
                <StarIcon filled={index + 1 <= starLevel} />
                <span
                  className="text-xs"
                  style={{ color: index + 1 <= starLevel ? '#FFB02E' : 'rgba(255,255,255,0.4)' }}
                >
                  {tier.label} earnings
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-white/10">
            <p className="text-xs text-cyan">
              Est. ${earnings.toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple compact card for the modal
const SimpleCard = ({ item, onClick, displayCost }) => {
  const [imageError, setImageError] = useState(false);
  const hasValidImage = item.imageUrl && !imageError;

  return (
    <div
      onClick={() => onClick(item)}
      className="bg-dark-blue/50 border border-white/20 rounded-lg overflow-hidden cursor-pointer hover:border-yellow/50 hover:bg-dark-blue/70 transition-all group"
    >
      {/* Image */}
      <div className="aspect-video relative overflow-hidden bg-dark-blue/30">
        {hasValidImage ? (
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {/* Category badge */}
        <span className="absolute top-2 right-2 px-2 py-0.5 bg-black/50 text-white/80 text-xs rounded">
          {item.category}
        </span>
      </div>

      {/* Info */}
      <div className="p-3">
        <h4 className="text-sm font-medium text-white truncate group-hover:text-yellow transition-colors">
          {item.title}
        </h4>
        {displayCost && (
          <span className="text-xs text-yellow mt-1 block">{displayCost} credits</span>
        )}
      </div>
    </div>
  );
};

export function CreatorProfileModal({
  isOpen,
  onClose,
  creator,
  accountNo,
  allCosyflows = [],
  onCosyflowSelect,
  getCostDisplay,
}) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter cosyflows by this creator's accountNo
  const creatorCosyflows = useMemo(() => {
    if (!accountNo) return [];
    return allCosyflows.filter(c => c.accountNo === accountNo);
  }, [allCosyflows, accountNo]);

  // Further filter by search query
  const filteredCosyflows = useMemo(() => {
    if (!searchQuery) return creatorCosyflows;
    const query = searchQuery.toLowerCase();
    return creatorCosyflows.filter(
      c =>
        c.title?.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query) ||
        c.category?.toLowerCase().includes(query)
    );
  }, [creatorCosyflows, searchQuery]);

  // Reset search when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleCardClick = (item) => {
    onClose();
    onCosyflowSelect?.(item);
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      center
      closeIcon={<CloseIcon />}
      classNames={{
        modal: 'creatorProfileModal',
        overlay: 'creatorProfileOverlay',
      }}
      styles={{
        modal: {
          background: '#190A4E',
          border: '2px solid rgba(0, 217, 255, 0.3)',
          borderRadius: '12px',
          padding: '0',
          maxWidth: '700px',
          width: '90vw',
          maxHeight: '80vh',
          overflow: 'hidden',
        },
        overlay: {
          background: 'rgba(0, 0, 0, 0.85)',
        },
        closeButton: {
          width: '28px',
          height: '28px',
          top: '12px',
          right: '12px',
        },
      }}
    >
      <div className="flex flex-col h-full max-h-[75vh]">
        {/* Header - Compact Creator Info */}
        <div className="bg-gradient-to-r from-dark-blue/80 to-purple-900/50 p-4 border-b border-white/10">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow/30 to-cyan/30 flex items-center justify-center text-white/70 flex-shrink-0">
              <UserIcon />
            </div>

            {/* Creator Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <h2 className="text-lg font-bold text-white truncate">{creator || 'Unknown Creator'}</h2>
                {/* Creator Earnings Level - disabled for now (demo feature)
                <CreatorStars accountNo={accountNo} />
                */}
              </div>
              <p className="text-sm text-white/60">{creatorCosyflows.length} cosyflow{creatorCosyflows.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Search - only show if many cosyflows */}
          {creatorCosyflows.length > 6 && (
            <div className="mt-3">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search cosyflows..."
                  className="w-full pl-9 pr-4 py-2 bg-dark-blue/50 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50"
                />
              </div>
            </div>
          )}
        </div>

        {/* Cosyflows Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredCosyflows.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-white/50 text-sm">
                {searchQuery ? 'No cosyflows found' : 'No cosyflows yet'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filteredCosyflows.map((item) => {
                const costFromCategory = getCostDisplay ? getCostDisplay(item.category) : null;
                const displayCost = costFromCategory || (item.credits_cost ? String(item.credits_cost) : null);

                return (
                  <SimpleCard
                    key={item.id}
                    item={item}
                    onClick={handleCardClick}
                    displayCost={displayCost}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default CreatorProfileModal;
